import { useEffect, useState } from 'hono/jsx';
import { SiteShell } from '../../components/SiteShell';
import { Spinner, ButtonSpinner } from '../../components/Spinner';
import { api, API_URL, formatRelativeTime } from '../../lib/api';
import { showToast } from '../../lib/toast';
import { useSite } from '../../lib/site';

interface DriveStatus {
  connected: boolean;
  folderUrl?: string;
  lastSync?: string;
  fileCount?: number;
}

export function SiteBackups({ siteId }: { siteId: string }) {
  const { site, loading: siteLoading } = useSite(siteId);
  const [drive, setDrive] = useState<DriveStatus | null>(null);
  const [driveLoading, setDriveLoading] = useState<boolean>(true);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [syncing, setSyncing] = useState<boolean>(false);

  async function loadDriveStatus() {
    setDriveLoading(true);
    try {
      const r = await api('/sites/' + siteId + '/drive/status');
      if (!r.ok) throw new Error('Failed to load Drive status');
      const status: DriveStatus = await r.json();
      setDrive(status);
    } catch (e) { setDrive({ connected: false }); console.error('Failed to load Drive status:', e); }
    finally { setDriveLoading(false); }
  }

  useEffect(() => { loadDriveStatus(); }, [siteId]);

  async function downloadBackup() {
    setDownloading(true);
    try {
      const r = await api('/sites/' + siteId + '/backup/download');
      if (!r.ok) { const err = await r.json().catch(() => ({})); throw new Error((err as any).error || 'Failed to download backup'); }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = r.headers.get('Content-Disposition');
      a.download = (cd && cd.match(/filename="(.+)"/) || [])[1] || 'backup.zip';
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
      showToast('success', 'Download started', 'Your backup is being downloaded');
    } catch (err: any) {
      showToast('error', 'Error', err?.message || 'Failed to download backup');
    } finally { setDownloading(false); }
  }

  async function syncDrive() {
    setSyncing(true);
    try {
      const r = await api('/sites/' + siteId + '/drive/push', { method: 'POST' });
      const result = await r.json().catch(() => ({}));
      if (!r.ok) {
        const redirect = (result as any).redirect || (result as any).authUrl;
        if (((result as any).needsReauth || (result as any).needsAuth) && redirect) {
          showToast('info', 'Reauthorization needed', 'Redirecting to Google…');
          setTimeout(() => { window.location.href = redirect; }, 1500);
          return;
        }
        throw new Error((result as any).error || 'Failed to sync');
      }
      showToast('success', 'Sync complete', `Synced ${(result as any).synced} files to Google Drive`);
      await loadDriveStatus();
    } catch (err: any) {
      showToast('error', 'Error', err?.message || 'Failed to sync');
    } finally { setSyncing(false); }
  }

  async function disconnectDrive() {
    if (!confirm('Disconnect Google Drive? Your files in Drive will remain intact.')) return;
    try {
      const r = await api('/sites/' + siteId + '/drive/disconnect', { method: 'DELETE' });
      if (!r.ok) { const err = await r.json().catch(() => ({})); throw new Error((err as any).error || 'Failed to disconnect'); }
      showToast('success', 'Disconnected', 'Google Drive disconnected');
      await loadDriveStatus();
    } catch (err: any) {
      showToast('error', 'Error', err?.message || 'Failed to disconnect');
    }
  }

  return (
    <SiteShell site={site} loading={siteLoading} active="backups">
      <div class="space-y-6">
        <div class="bg-dark-800 border border-slate-700/50 rounded-xl p-6">
          <h2 class="text-lg font-semibold text-slate-100 mb-1">Download backup</h2>
          <p class="text-xs text-slate-400 mb-3">Get a ZIP archive of every file on this site, exactly as deployed.</p>
          <button
            type="button" onClick={downloadBackup} disabled={downloading}
            class="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-dark-700 border border-slate-600/50 rounded-lg text-sm font-medium text-slate-200 hover:bg-dark-600 hover:border-slate-500 transition-colors disabled:opacity-60"
          >
            {downloading ? <><ButtonSpinner /> Preparing…</> : <>
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>
              Download Backup ZIP
            </>}
          </button>
        </div>

        <div class="bg-dark-800 border border-slate-700/50 rounded-xl p-6">
          <div class="flex items-center gap-2 mb-1">
            <svg class="w-5 h-5 text-slate-400" viewBox="0 0 24 24" fill="currentColor"><path d="M12.01 1.485c-2.082 0-3.754.02-3.743.047.01.02 1.708 3.001 3.774 6.62l3.76 6.574h3.76c2.081 0 3.753-.02 3.742-.047-.005-.02-1.708-3.001-3.775-6.62l-3.76-6.574h-3.758zm-5.516 6.574L.477 14.633l1.889 3.287 6.016 4.795 6.016-10.574H8.382l-1.887-3.287z"/></svg>
            <h2 class="text-lg font-semibold text-slate-100">Google Drive auto-sync</h2>
            <span class="text-xs text-slate-500">(Optional)</span>
          </div>
          <p class="text-xs text-slate-400 mb-4">One-way sync: site files are mirrored into a folder in your Google Drive. You own the files.</p>
          {driveLoading ? (
            <div class="flex items-center justify-center py-4"><Spinner size="sm" /></div>
          ) : drive?.connected ? (
            <div>
              <div class="flex items-center gap-2 mb-3"><div class="w-2 h-2 bg-emerald-400 rounded-full"></div><span class="text-sm text-emerald-400">Connected</span></div>
              <div class="space-y-3 mb-3">
                <div>
                  <label class="block text-xs text-slate-500 mb-1">Folder URL</label>
                  <div class="flex gap-2">
                    <input type="text" readOnly value={drive.folderUrl || ''} class="flex-1 px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/50" />
                    <a href={drive.folderUrl || '#'} target="_blank" rel="noopener" class="px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-slate-400 hover:text-cyan-400 transition-colors" title="Open in Drive">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/></svg>
                    </a>
                  </div>
                </div>
                <div class="flex items-center justify-between text-xs bg-dark-900 rounded p-2"><span class="text-slate-500">Last sync:</span><span class="text-slate-300">{drive.lastSync ? formatRelativeTime(drive.lastSync) : 'Never'}</span></div>
                <div class="flex items-center justify-between text-xs bg-dark-900 rounded p-2"><span class="text-slate-500">Files synced:</span><span class="text-slate-300">{drive.fileCount ?? 0}</span></div>
              </div>
              <div class="flex gap-2">
                <button type="button" onClick={syncDrive} disabled={syncing} class="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-sm font-medium text-slate-200 hover:bg-dark-600 transition-colors disabled:opacity-60">
                  {syncing ? <><ButtonSpinner /> Syncing…</> : <>
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
                    Sync Now
                  </>}
                </button>
                <button type="button" onClick={disconnectDrive} class="px-3 py-2 text-sm font-medium text-red-400 hover:bg-red-500/10 rounded-lg transition-colors">Disconnect</button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => { window.location.href = `${API_URL}/sites/${siteId}/drive/connect`; }}
              class="w-full flex items-center justify-center gap-2 px-4 py-2 bg-cyan-500 text-dark-900 rounded-lg text-sm font-medium hover:bg-cyan-400 transition-colors"
            >Connect with Google Drive</button>
          )}
        </div>
      </div>
    </SiteShell>
  );
}
